/* lib/phone.js — المصدر المركزي الوحيد لأرقام الجوال بالواجهة.
   محرك Google libphonenumber (libphonenumber-js) + قواعد جوال خليجية/عربية صارمة فوقه.
   التخزين دائماً E.164 — مثل ‎+9665XXXXXXXX للسعودية و‎+974XXXXXXXX لقطر. */
import {
  getCountries, getCountryCallingCode, parsePhoneNumberFromString,
  AsYouType, getExampleNumber,
} from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";

/* قواعد صارمة فوق المكتبة: طول الرقم المحلي + بادئات الجوال المسموحة (مرآة services/phone.py) */
export const GCC_RULES = {
  974: { len: [8], mobile: ["3", "5", "6", "7"], ar: "قطر", en: "Qatar" },
  966: { len: [9], mobile: ["5"], ar: "السعودية", en: "Saudi Arabia" },
  971: { len: [9], mobile: ["5"], ar: "الإمارات", en: "UAE" },
  973: { len: [8], mobile: ["3", "6"], ar: "البحرين", en: "Bahrain" },
  965: { len: [8], mobile: ["4", "5", "6", "9"], ar: "الكويت", en: "Kuwait" },
  968: { len: [8], mobile: ["7", "9"], ar: "عُمان", en: "Oman" },
  20: { len: [10], mobile: ["1"], ar: "مصر", en: "Egypt" },
  961: { len: [7, 8], mobile: ["3", "7", "8"], ar: "لبنان", en: "Lebanon" },
};

/* ربط دولة الفرع (country_code المخزن بقاعدة البيانات مثل SA/QA) → مفتاح الاتصال */
export function dialForCountry(iso) {
  try { return iso ? String(getCountryCallingCode(String(iso).toUpperCase())) : ""; }
  catch { return ""; }
}

/* مفتاح → الدولة الأساسية (للمفاتيح المشتركة مثل +1 و+44) */
const PRIORITY_ISO = { 1: "US", 7: "RU", 39: "IT", 44: "GB", 47: "NO", 61: "AU", 64: "NZ", 212: "MA", 262: "RE", 358: "FI", 590: "GP", 599: "CW" };
const DIAL_TO_ISO = {};
for (const iso of getCountries()) {
  const d = String(getCountryCallingCode(iso));
  if (!DIAL_TO_ISO[d]) DIAL_TO_ISO[d] = iso;
}
Object.entries(PRIORITY_ISO).forEach(([d, iso]) => { DIAL_TO_ISO[d] = iso; });
export const isoForDial = (dial) => DIAL_TO_ISO[String(dial)] || "";

const ALL_DIALS = Object.keys(DIAL_TO_ISO).sort((a, b) => b.length - a.length);

export const flagEmoji = (iso) =>
  String(iso || "").toUpperCase().replace(/./g, (ch) => String.fromCodePoint(127397 + ch.charCodeAt(0)));

const toLatin = (s) => String(s || "").replace(/[٠-٩۰-۹]/g, (d) => {
  const i = "٠١٢٣٤٥٦٧٨٩".indexOf(d);
  return i >= 0 ? String(i) : String("۰۱۲۳۴۵۶۷۸۹".indexOf(d));
});

/* قائمة كل دول العالم — علم + اسم محلي + مفتاح (الدول العربية والخليجية مثبّتة أولاً) */
const PINNED = ["QA", "SA", "AE", "KW", "BH", "OM", "EG", "LB", "JO", "IQ", "PS", "SY", "YE", "SD", "MA", "DZ", "TN", "LY", "TR", "GB", "US"];
const _listCache = {};
export function countryList(lang = "ar") {
  if (_listCache[lang]) return _listCache[lang];
  let dn = null, dnEn = null;
  try {
    dn = new Intl.DisplayNames([lang === "ar" ? "ar" : "en"], { type: "region" });
    dnEn = new Intl.DisplayNames(["en"], { type: "region" });
  } catch { /* متصفح قديم — نعرض رمز الدولة */ }
  const safe = (f, iso) => { try { return f?.of(iso) || iso; } catch { return iso; } };
  const all = getCountries().map((iso) => ({
    iso,
    dial: String(getCountryCallingCode(iso)),
    flag: flagEmoji(iso),
    name: safe(dn, iso),
    en: safe(dnEn, iso),
  }));
  const pinned = PINNED.map((p) => all.find((c) => c.iso === p)).filter(Boolean);
  const rest = all.filter((c) => !PINNED.includes(c.iso))
    .sort((a, b) => a.name.localeCompare(b.name, lang === "ar" ? "ar" : "en"));
  _listCache[lang] = [...pinned, ...rest];
  return _listCache[lang];
}

/* تنسيق الرقم المحلي للعرض (تجميع الأرقام حسب الدولة) */
export function formatNationalDigits(dial, digits) {
  const d = String(digits || "").replace(/\D/g, "");
  if (!d) return "";
  try {
    const out = new AsYouType().input(`+${dial}${d}`);
    return out.replace(new RegExp(`^\\+${dial}\\s*`), "").trim() || d;
  } catch { return d; }
}

/* مثال رقم حقيقي للدولة — placeholder */
export function examplePlaceholder(dial) {
  const iso = isoForDial(dial);
  if (!iso) return "";
  try {
    const ex = getExampleNumber(iso, examples);
    return ex ? formatNationalDigits(dial, String(ex.nationalNumber)) : "";
  } catch { return ""; }
}

/* تفكيك رقم كامل → {cc, num} — صيغة محلية بلا + تُفسَّر بمفتاح دولة التوصيل (لا +974 ثابت) */
export function splitPhone(full, defaultDial = "974") {
  let d = toLatin(full).replace(/[^0-9+]/g, "");
  const intl = d.startsWith("+") || d.startsWith("00");
  if (d.startsWith("+")) d = d.slice(1);
  while (d.startsWith("00")) d = d.slice(2);
  if (!d) return { cc: defaultDial, num: "" };
  if (intl) {
    const cc = ALL_DIALS.find((c) => d.startsWith(c));
    if (cc) return { cc, num: d.slice(cc.length).replace(/^0+/, "") };
    return { cc: defaultDial, num: d };
  }
  return { cc: defaultDial, num: d.replace(/^0+/, "") };
}

export function joinPhone(dial, num) {
  const n = String(num || "").replace(/[^0-9]/g, "").replace(/^0+/, "");
  return n ? `+${dial}${n}` : "";
}

/* تحقق كامل: قواعدنا الصارمة أولاً ثم Google libphonenumber كطبقة نهائية.
   يعيد {ok, e164, cc, ar, en} */
export function validatePhone(full, defaultDial = "974") {
  let d = toLatin(full).replace(/[^0-9+]/g, "");
  const intl = d.startsWith("+") || d.startsWith("00");
  if (d.startsWith("+")) d = d.slice(1);
  while (d.startsWith("00")) d = d.slice(2);
  if (!d) return { ok: false, e164: "", cc: "", ar: "أدخل رقم الجوال", en: "Enter the phone number" };
  if (!intl) d = `${defaultDial}${d.replace(/^0+/, "")}`;
  const cc = ALL_DIALS.find((c) => d.startsWith(c)) || "";
  if (cc) d = cc + d.slice(cc.length).replace(/^0+/, ""); // صفر بادئ محلي بعد المفتاح → يُحذف
  if (d.startsWith("0"))
    return { ok: false, e164: "", cc, ar: "الرقم يبدأ بصفر — اختر رمز الدولة واكتب الرقم بدون الصفر الأول", en: "Number starts with 0 — pick the country code and drop the leading zero" };
  if (d.length < 8) return { ok: false, e164: "", cc, ar: "الرقم ناقص — اكتبه كاملاً", en: "Number is too short" };
  if (d.length > 15) return { ok: false, e164: "", cc, ar: "الرقم أطول من المسموح", en: "Number is too long" };
  const rule = GCC_RULES[cc];
  if (rule) {
    const local = d.slice(cc.length);
    if (!rule.len.includes(local.length))
      return { ok: false, e164: "", cc,
        ar: `رقم ${rule.ar} يتكون من ${rule.len.join("/")} أرقام بعد رمز الدولة — المدخل ${local.length}`,
        en: `${rule.en} numbers have ${rule.len.join("/")} digits after the code (got ${local.length})` };
    if (!rule.mobile.some((p) => local.startsWith(p)))
      return { ok: false, e164: "", cc,
        ar: `هذا ليس رقم جوال ${rule.ar} — الجوال يبدأ بـ${rule.mobile.join("/")}`,
        en: `Not a ${rule.en} mobile — mobiles start with ${rule.mobile.join("/")}` };
  }
  const parsed = parsePhoneNumberFromString("+" + d);
  if (!parsed || !parsed.isValid()) {
    const hint = examplePlaceholder(cc || defaultDial);
    return { ok: false, e164: "+" + d, cc,
      ar: `الرقم غير صحيح لهذه الدولة${hint ? ` — مثال: ‎${hint}` : ""}`,
      en: `Invalid number for this country${hint ? ` — e.g. ${hint}` : ""}` };
  }
  return { ok: true, e164: parsed.number, cc: cc || String(parsed.countryCallingCode), ar: "", en: "" };
}
